import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Nav, Image } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSignOutAlt, 
  faUser, 
  faHome, 
  faUsers, 
  faTabletAlt, 
  faFileAlt, 
  faCog,
  faBars,
  faTimes,
  faChevronLeft,
  faSignInAlt,
  faUserPlus,
  faClipboardList,
  faCalendarAlt,
  faSignInAlt as faSignIn
} from '@fortawesome/free-solid-svg-icons';
import logo from '../../logo-bachiller-digital.png';
import './Sidebar.css';

const Sidebar = ({ currentUser, isAdmin, logOut }) => {
  // Inicialmente colapsado en móviles, expandido en escritorio
  const [collapsed, setCollapsed] = useState(() => {
    return window.innerWidth <= 990;
  });
  
  useEffect(() => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      if (collapsed) {
        mainContent.style.marginLeft = '100px';
        mainContent.style.width = 'calc(100% - 100px)';
      } else {
        mainContent.style.marginLeft = '250px';
        mainContent.style.width = 'calc(100% - 250px)';
      }
    }
  }, [collapsed]);
  const location = useLocation();

  // Toggle sidebar
  const toggleSidebar = () => {
    setCollapsed(prev => !prev);
  };
  
  // Colapsar automáticamente al cambiar de ruta en móviles
  useEffect(() => {
    if (window.innerWidth <= 990) {
      setCollapsed(true);
    }
  }, [location.pathname]);
  
  // Cerrar el menú al hacer clic fuera de él en móviles
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth <= 990) {
        const sidebar = document.querySelector('.sidebar-container');
        const toggleButton = document.querySelector('.sidebar-toggle');
        
        if (sidebar && !sidebar.contains(event.target) && 
            toggleButton && !toggleButton.contains(event.target)) {
          setCollapsed(true);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cerrar el menú al hacer clic en un enlace en móviles
  const handleNavLinkClick = () => {
    if (window.innerWidth <= 990) { // Ancho típico para menú móvil
      setCollapsed(true);
    }
  };

  const NavItem = ({ to, icon, children }) => (
    <Nav.Item className="w-100 mb-2">
      <Nav.Link 
        as={Link} 
        to={to}
        onClick={handleNavLinkClick}
        className={`d-flex align-items-center ${location.pathname === to ? 'active' : ''}`}
      >
        <FontAwesomeIcon icon={icon} className="me-3" />
        {!collapsed && <span>{children}</span>}
      </Nav.Link>
    </Nav.Item>
  );
  
  const AuthButtons = () => (
    <div className="auth-buttons mt-auto">
      <Nav.Item className="w-100 mb-2">
        <Nav.Link 
          as={Link} 
          to="/login" 
          onClick={handleNavLinkClick}
          className="d-flex align-items-center"
        >
          <FontAwesomeIcon icon={faSignInAlt} className="me-3" />
          {!collapsed && <span>Iniciar Sesión</span>}
        </Nav.Link>
      </Nav.Item>
      <Nav.Item className="w-100 mb-2">
        <Nav.Link 
          as={Link} 
          to="/register" 
          onClick={handleNavLinkClick}
          className="d-flex align-items-center"
        >
          <FontAwesomeIcon icon={faUserPlus} className="me-3" />
          {!collapsed && <span>Registrarse</span>}
        </Nav.Link>
      </Nav.Item>
    </div>
  );

  return (
    <>
      <button 
        className="sidebar-toggle d-lg-none" 
        onClick={toggleSidebar}
        aria-label={collapsed ? 'Abrir menú' : 'Cerrar menú'}
      >
        <FontAwesomeIcon icon={faBars} />
      </button>
      
      <div 
        className={`sidebar-overlay ${!collapsed ? 'open' : ''} d-lg-none`} 
        onClick={toggleSidebar}
      ></div>
      
      <div className={`sidebar-container ${collapsed ? 'closed' : 'open'}`}>
        <div className="sidebar-header d-flex align-items-center p-3">
          <div className="sidebar-brand-container">
            <Image src={logo} alt="Bachiller Digital" height="99" className="me-2" />
            <span className="sidebar-brand"></span>
          </div>
          <button 
            className="sidebar-close" 
            onClick={toggleSidebar}
            aria-label={collapsed ? 'Abrir menú' : 'Cerrar menú'}
          >
            <FontAwesomeIcon icon={collapsed ? faChevronLeft : faChevronLeft} className={collapsed ? 'rotate-180' : ''} />
          </button>
        </div>

        <div className="sidebar-content">
          <Nav className="flex-column px-3 pt-3">
            {currentUser && (
              <>
                {!isAdmin && (
                  <NavItem to="/inscripcion" icon={faFileAlt}>
                    Inscripción
                  </NavItem>
                )}
                
                {isAdmin && (
                  <>
                    <NavItem to="/estudiantes" icon={faUsers}>
                      Estudiantes
                    </NavItem>
                    <NavItem to="/admin/usuarios" icon={faUser}>
                      Usuarios
                    </NavItem>
                    <NavItem to="/admin/periodos" icon={faCalendarAlt}>
                      Gestión de Períodos
                    </NavItem>
                    <NavItem to="/tablet-ingreso" icon={faSignIn}>
                      Ingreso de Tablets
                    </NavItem>
                    <NavItem to="/acta" icon={faClipboardList}>
                      Acta de Entrega
                    </NavItem>
                  </>
                )}
              </>
            )}
          </Nav>

          <div className="sidebar-footer mt-auto p-3">
            {currentUser ? (
              <>
                <Link to="/profile" className="text-decoration-none text-white w-100 mb-3 d-block">
                  <div className="user-info d-flex align-items-center">
                    <div className="user-avatar bg-white text-primary rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '40px', height: '40px' }}>
                      <FontAwesomeIcon icon={faUser} />
                    </div>
                    {!collapsed && (
                      <div className="user-details">
                        <div className="user-name">{currentUser.username}</div>
                        <div className="user-role small">{isAdmin ? 'Administrador' : 'Usuario'}</div>
                      </div>
                    )}
                  </div>
                </Link>
                <button 
                  className="btn btn-outline-light w-100" 
                  onClick={logOut}
                >
                  <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" /> 
                  {!collapsed && 'Cerrar Sesión'}
                </button>
              </>
            ) : (
              <div className="mt-auto">
                <AuthButtons />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
